import { useMemo } from 'react'
import { useDocumentDataOnce } from 'react-firebase-hooks/firestore'
import {
  categoryConverter,
  chapterConverter,
  macroConverter,
  pageConverter,
  referenceConverter,
  textbookConverter,
  textbookRef,
} from 'web_core/firebase'
import { useMacros, useQueryData, useReferences } from 'web_core/hooks'
import { useCategory } from 'web_core/hooks/models/useCategory'
import { useChapters } from 'web_core/hooks/models/useChapters'
import { usePages } from 'web_core/hooks/models/usePages'
import { deserialize, deserializeArray, isBlank } from 'web_core/utils'
import { TextbookPageProps } from '~/static_props/textbook'

export const useTextbookPageProps = ({
  textbook: staticTextbook,
  chapters: staticChapters,
  pages: staticPages,
  currentPageId: staticCurrentPageId,
  category: staticCategory,
  references: staticReferences,
  macros: staticMacros,
}: TextbookPageProps) => {
  const id = useQueryData()
  const [textbook, loadingTextbook] = useDocumentDataOnce(
    !isBlank(id) && id !== '' ? textbookRef(id) : null,
    {
      initialValue: staticTextbook
        ? deserialize(staticTextbook, { ref: textbookConverter })
        : undefined,
    }
  )
  const { pages, loading: loadingPages } = usePages(
    textbook,
    staticPages
      ? deserializeArray(staticPages, { ref: pageConverter })
      : undefined
  )
  const { chapters, loading: loadingChapters } = useChapters(
    textbook,
    staticChapters
      ? deserializeArray(staticChapters, { ref: chapterConverter })
      : undefined
  )
  const currentPageId = useQueryData('page_id') ?? staticCurrentPageId
  const currentPage = useMemo(() => {
    return pages?.find((p) => p.id === currentPageId)
  }, [pages, currentPageId])

  const { category, loading: loadingCategory } = useCategory(
    textbook?.category_id,
    staticCategory
      ? deserialize(staticCategory, { ref: categoryConverter })
      : undefined
  )
  const { references, loading: loadingReferences } = useReferences(
    textbook ? textbook.ref : undefined,
    true,
    staticReferences
      ? deserializeArray(staticReferences, { ref: referenceConverter })
      : undefined
  )
  const { macros, loading: loadingMacros } = useMacros(
    textbook ? textbook.ref : undefined,
    true,
    staticMacros
      ? deserializeArray(staticMacros, { ref: macroConverter })
      : undefined
  )

  const isLoading =
    (!isBlank(id) && id !== '') ||
    loadingTextbook ||
    loadingPages ||
    loadingChapters ||
    loadingCategory ||
    loadingReferences ||
    loadingMacros

  const isNotFound =
    !isLoading &&
    (!textbook ||
      !pages ||
      !category ||
      !chapters ||
      !references ||
      !macros ||
      (currentPageId !== null && currentPageId !== '' && !currentPage))

  return {
    isNotFound,
    textbook,
    pages,
    chapters,
    currentPage,
    category,
    macros,
    references,
  }
}
